import styled from '@emotion/styled';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import FolderIcon from '@mui/icons-material/FolderOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MessageIcon from '@mui/icons-material/MessageOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlineOutlined';
import PetsIcon from '@mui/icons-material/Pets';
import RequestPageIcon from '@mui/icons-material/RequestPageOutlined';
import { Box, Button, ButtonProps, ListItem, MenuList, Theme, Typography, useTheme } from '@mui/material';
import Link from 'next/link';

import {
	UserPortalSidebarListItemBottomStyles,
	UserPortalSidebarListItemTopStyles,
	UserPortalSidebarListStyles,
} from './UserPortalSidebar.styles';

import { useAppContext } from '@shared-components-web';
import { APP_PATH } from '@src/constants';
import useCollectionsGuard from '@src/hooks/useCollectionsGuard';

export type UserPortalPage = {
	title: string;
	icon: React.ElementType;
	url: string;
	isVisible?: boolean;
};

export const getUserPortalPages = (featureList: string[] = []): { [key: string]: UserPortalPage } => ({
	dashboard: {
		title: 'Dashboard',
		icon: HomeOutlinedIcon,
		url: '/user-portal',
		isVisible: true,
	},
	pets: {
		title: 'Pets',
		icon: PetsIcon,
		url: '/user-portal/pets',
		isVisible: true,
	},
	appointments: {
		title: 'Appointments',
		icon: CalendarTodayOutlinedIcon,
		url: '/user-portal/appointments',
		isVisible: true,
	},
	medicalRecords: {
		title: 'Medical Records',
		icon: FolderIcon,
		url: '/user-portal/medical-records',
		isVisible: featureList.includes('showMedicalRecords'),
	},
	billing: {
		title: 'Billing',
		icon: RequestPageIcon,
		url: '/user-portal/billing',
		isVisible: false,
	},
	messages: {
		title: 'Messages',
		icon: MessageIcon,
		url: '/user-portal/messages',
		isVisible: false,
	},
	account: {
		title: 'Account',
		icon: PersonIcon,
		url: '/user-portal/account',
		isVisible: true,
	},
});

export const getActivePage = (pages: { [key: string]: UserPortalPage }): string | null => {
	if (typeof globalThis === 'undefined') return 'dashboard';
	const path = globalThis.location.pathname;
	const pageKey =
		path === '/book-an-appointment/user-portal/'
			? 'dashboard'
			: Object.keys(pages).find((key) => {
					if (key === 'dashboard') return false;
					const page = pages[key];
					return path.includes(page.url);
			  });
	return pageKey || 'dashboard';
};

export const UserPortalLink = styled(Link)(() => ({
	width: '100%',
}));

export const UserPortalActiveButton = styled(Button)(({ theme }: { theme: Theme }) => ({
	color: `${theme.palette.text.light}`,
	cursor: 'default',
	backgroundColor: `${theme.palette.text.primary}`,
	'&:hover': {
		backgroundColor: `${theme.palette.text.primary}`,
	},
}));

export const UserPortalInactiveButton = styled(Button)(({ theme }: { theme: Theme }) => ({
	color: `${theme.palette.text.black}`,
	backgroundColor: `${theme.palette.backgrounds.white}`,
	'&:hover': {
		backgroundColor: `${theme.palette.backgrounds.neutral}`,
	},
}));

export function UserPortalSidebarList({
	allowScheduleButton = false,
	onClose,
}: Readonly<{
	allowScheduleButton?: boolean;
	onClose?: () => void;
}>) {
	const theme = useTheme();
	const { config } = useAppContext();
	const featureList = config?.featureFlags?.featureList || [];
	const pages = getUserPortalPages(featureList);
	const activePage = getActivePage(pages);
	const allowSignOut = true;

	const onNavLinkClick = (e: React.MouseEvent, isActive: boolean) => {
		if (isActive) {
			e.preventDefault();
			return false;
		}

		if (onClose) onClose();
	};

	const linkProps = (page: UserPortalPage, key: string) => ({
		href: `${page.url}`,
		'data-test-id': `user-portal-sidebar-link-${key}`,
	});

	const buttonProps = (isActive: boolean) =>
		({
			fullWidth: true,
			sx: {
				justifyContent: 'flex-start',
				textTransform: 'none',
				padding: '0.75rem 1rem',
			},
			onClick: (e) => onNavLinkClick(e, isActive),
		} as ButtonProps);

	const guard = useCollectionsGuard();

	const onScheduleClick = (e: React.MouseEvent) => {
		if (guard(e)) return;
		onClose?.();
	};

	return (
		<>
			{pages &&
				Object.keys(pages)
					.filter((key) => pages[key].isVisible)
					.map((key) => {
						const page = pages[key];
						const isActive = activePage === key;

						if (!page.isVisible) return;

						return (
							<ListItem
								data-test-id={`user-portal-sidebar-nav-${key}`}
								sx={UserPortalSidebarListItemTopStyles}
								key={key}
								disablePadding
							>
								{isActive && (
									<UserPortalLink {...linkProps(page, key)}>
										<UserPortalActiveButton
											data-test-id={`user-portal-sidebar-button-${key}`}
											theme={theme}
											startIcon={<page.icon />}
											{...buttonProps(isActive)}
										>
											<Typography fontWeight="700">{page.title}</Typography>
										</UserPortalActiveButton>
									</UserPortalLink>
								)}
								{!isActive && (
									<UserPortalLink {...linkProps(page, key)}>
										<UserPortalInactiveButton
											data-test-id={`user-portal-sidebar-button-${key}`}
											theme={theme}
											startIcon={<page.icon />}
											{...buttonProps(isActive)}
										>
											<Typography fontWeight="700" fontSize="1rem">
												{page.title}
											</Typography>
										</UserPortalInactiveButton>
									</UserPortalLink>
								)}
							</ListItem>
						);
					})}

			{allowScheduleButton || allowSignOut ? <Box sx={{ flexGrow: 1 }} /> : null}
			{allowScheduleButton && (
				<ListItem sx={UserPortalSidebarListItemBottomStyles} disablePadding>
					<Button
						fullWidth
						size="M"
						sx={{
							justifyContent: 'center',
							textTransform: 'none',
						}}
						variant={'primaryDefault'}
						href={APP_PATH}
						onClick={onScheduleClick}
						data-test-id={`user-portal-sidebar-link-book`}
					>
						<Typography fontWeight="700" fontSize="1rem">
							Schedule appointment
						</Typography>
					</Button>
				</ListItem>
			)}
			{allowSignOut && (
				<ListItem sx={UserPortalSidebarListItemBottomStyles} disablePadding>
					<Button
						fullWidth
						size="M"
						sx={{
							justifyContent: 'flex-start',
							textTransform: 'none',
						}}
						variant="tertiaryDefault"
						href={`${APP_PATH}/logout`}
						data-test-id="user-portal-sidebar-link-signout"
					>
						<LogoutIcon sx={{ marginRight: '0.75rem' }} />
						<Typography fontWeight="700" fontSize="1rem">
							Sign out
						</Typography>
					</Button>
				</ListItem>
			)}
		</>
	);
}

export default function UserPortalSidebar() {
	return (
		<MenuList data-test-id="user-portal-sidebar-list" sx={UserPortalSidebarListStyles}>
			<UserPortalSidebarList allowScheduleButton />
		</MenuList>
	);
}
